import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { getBucketScoreColors, scoreToBarPosition } from "./sentimentScale";
import TopicItemTable from "./TopicItemTable";

function ScoreBadge({ score }) {
  const colors = getBucketScoreColors(score);
  const label = score === null || score === undefined ? "—" : score > 0 ? `+${score.toFixed(1)}` : score.toFixed(1);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 40, px: 1.1, py: 0.4, borderRadius: "12px", fontSize: 12.5, fontWeight: 700, bgcolor: colors.bg, color: colors.fg }}>
      {label}
    </Box>
  );
}

// Diverging bar: grows from a fixed center (0) marker toward the left for a
// negative score, toward the right for a positive one — length proportional
// to magnitude, colored by the same sign/magnitude tiers as the ScoreBadge
// next to it. Replaces the old fixed full-width gradient + tick-position
// indicator (still used as-is in SummaryBar.js's overall-score bar).
//
// `compact` renders it shorter (height only — width stays BAR_TRACK_MAX_WIDTH
// so it keeps lining up with the -3/-1.5/0/+1.5/+3 header scale above it).
// Used for subsection rows, so the shorter track reads as "nested under a
// topic row", on top of the indent/guide-line already doing that job.
function DivergingBar({ score, compact = false }) {
  const pos = scoreToBarPosition(score); // 0-100, 50 = zero
  const barLeft = Math.min(pos, 50);
  const barWidth = Math.abs(pos - 50);
  const isPositive = pos > 50;
  const fillColor = getBucketScoreColors(score).fg;
  const trackHeight = compact ? BAR_TRACK_HEIGHT_COMPACT : BAR_TRACK_HEIGHT;

  return (
    <Box sx={{ position: "relative", height: trackHeight, borderRadius: "4px", bgcolor: "#eef1f4", width: BAR_TRACK_MAX_WIDTH, mx: "auto" }}>
      {barWidth > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: `${barLeft}%`,
            width: `${barWidth}%`,
            height: trackHeight,
            // Square off the edge touching the 0 line — rounding it made the
            // bar optically read as stopping short of center even though it
            // was mathematically flush; only the outer (far) end stays
            // rounded, so the bar visibly attaches to the 0 line.
            borderRadius: isPositive ? "0 4px 4px 0" : "4px 0 0 4px",
            bgcolor: fillColor,
          }}
        />
      )}
      {/* -1.5 / +1.5 tick marks — lighter, flush with the track, drawn over the
          fill so they stay visible even where a bar covers that position. */}
      <Box sx={{ position: "absolute", top: 0, left: "25%", width: 1.5, height: trackHeight, bgcolor: "#c3c9d1", borderRadius: "1px" }} />
      <Box sx={{ position: "absolute", top: 0, left: "75%", width: 1.5, height: trackHeight, bgcolor: "#c3c9d1", borderRadius: "1px" }} />
      {/* 0 tick mark — darker, overhangs the track top/bottom so it reads as the anchor */}
      <Box sx={{ position: "absolute", top: -3, left: "50%", width: 1.5, height: trackHeight + 6, bgcolor: "#1a2027", borderRadius: "1px" }} />
    </Box>
  );
}

// Fixed (not max-) width for the scale/bar column, centered via mx:"auto"
// within its otherwise full-width 1fr grid track — wide enough to read
// comfortably, narrow enough not to stretch edge-to-edge of the card. Must
// stay a definite width rather than maxWidth: both boxes' only children are
// position:absolute (removed from flow), so they have no in-flow content to
// size against — maxWidth alone would let them collapse to 0 and auto-center
// at nothing. Shared between the header's scale labels and each row's
// DivergingBar so the -3/-1.5/0/+1.5/+3 marks stay aligned with the bar
// beneath them.
const BAR_TRACK_MAX_WIDTH = 600;
const BAR_TRACK_HEIGHT = 14;
// Subsection rows' bar height — same width as the main track (see
// DivergingBar's `compact`), just thinner.
const BAR_TRACK_HEIGHT_COMPACT = 9;

const GRID_TEMPLATE_COLUMNS_EXPANDABLE = "244px 90px 1fr";
const GRID_TEMPLATE_COLUMNS_FLAT = "220px 90px 1fr";

const SORT_MODES = [
  { key: "default", label: "Default" },
  { key: "positive", label: "Positive" },
  { key: "negative", label: "Negative" },
];

// Reorders rows only — never hides any topic. "default" is whatever order the
// caller passed in (today: fixed topic order). "positive"/"negative" sort by
// score descending/ascending; unscored topics (score null) always sort last,
// in every mode, since they're neither positive nor negative signal.
function sortRows(rows, sortMode) {
  if (sortMode === "default") return rows;
  const scored = rows.filter((r) => typeof r.score === "number");
  const unscored = rows.filter((r) => typeof r.score !== "number");
  scored.sort((a, b) => (sortMode === "positive" ? b.score - a.score : a.score - b.score));
  return [...scored, ...unscored];
}

function SortToggle({ sortMode, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75, mb: 2 }}>
      {SORT_MODES.map((mode) => {
        const active = sortMode === mode.key;
        return (
          <Box
            key={mode.key}
            onClick={() => onChange(mode.key)}
            sx={{
              px: 1.5,
              py: 0.6,
              borderRadius: "8px",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              userSelect: "none",
              border: "1px solid",
              borderColor: active ? "#1a2027" : "#e4e7ec",
              bgcolor: active ? "#1a2027" : "#fff",
              color: active ? "#fff" : "#475467",
            }}
          >
            {mode.label}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Per-topic brand sentiment breakdown, with a diverging scale-position bar
 * per row. Brand-only — no category/overall columns.
 *
 * When `expandable` is true, an arrow sits left of each topic name — click
 * it (or the row) to expand that topic's item table inline, right under
 * the row. When false (legacy layout), rows are flat and DetailView lists
 * every topic's table separately underneath the whole breakdown instead.
 */
export default function TopicBucketBreakdown({ rows, expandable = false }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [sortMode, setSortMode] = useState("default");
  // Subsections (e.g. "Purchase Enablers" / "Purchase Barriers" within a
  // combined row) default open — this tracks which are individually folded
  // shut. Keyed by sub.label, which is unique per combined-row type, so a
  // flat Set is safe without nesting per parent row.
  const [collapsedSubLabels, setCollapsedSubLabels] = useState(() => new Set());

  const gridTemplateColumns = expandable ? GRID_TEMPLATE_COLUMNS_EXPANDABLE : GRID_TEMPLATE_COLUMNS_FLAT;

  const toggleRow = (topicKey) => {
    setExpandedKey((prev) => (prev === topicKey ? null : topicKey));
  };

  const toggleSubsection = (label) => {
    setCollapsedSubLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const sortedRows = useMemo(() => sortRows(rows, sortMode), [rows, sortMode]);

  return (
    <Box id="cm-tour-topic-breakdown" sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0px 3.5px 5.5px 0px rgba(0,0,0,0.05)", p: "22px 26px" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1a2027", mb: 0.5 }}>Topic Bucket Breakdown</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#667085", mb: 2 }}>
        Scale &minus;3 (very negative) to +3 (very positive)
        {expandable && " — click a topic to see its items"}
      </Typography>

      <SortToggle sortMode={sortMode} onChange={setSortMode} />

      <Box sx={{ display: "grid", gridTemplateColumns, gap: 1.75, pb: 1.25, borderBottom: "1px solid #eef1f4", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#98a2b3" }}>
        <div>Topic</div>
        <div>Sentiment Score</div>
        <Box sx={{ position: "relative", height: "1em", width: BAR_TRACK_MAX_WIDTH, mx: "auto" }}>
          <span style={{ position: "absolute", left: 0 }}>&minus;3</span>
          <span style={{ position: "absolute", left: "25%", transform: "translateX(-50%)" }}>&minus;1.5</span>
          <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>0</span>
          <span style={{ position: "absolute", left: "75%", transform: "translateX(-50%)" }}>+1.5</span>
          <span style={{ position: "absolute", right: 0 }}>+3</span>
        </Box>
      </Box>

      {sortedRows.map((row, i) => {
        const isExpanded = expandable && expandedKey === row.topicKey;
        return (
          <Box key={row.topicKey}>
            <Box
              onClick={expandable ? () => toggleRow(row.topicKey) : undefined}
              sx={{
                display: "grid",
                gridTemplateColumns,
                gap: 1.75,
                alignItems: "center",
                p: "13px 0",
                borderBottom: !isExpanded && i === rows.length - 1 ? "none" : "1px solid #f5f6f8",
                cursor: expandable ? "pointer" : "default",
                "&:hover": expandable ? { bgcolor: "#fafbfc" } : undefined,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {expandable && (
                  <KeyboardArrowRightIcon
                    sx={{ fontSize: 18, color: "#98a2b3", transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
                  />
                )}
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#1a2027" }}>{row.label}</Typography>
              </Box>
              <ScoreBadge score={row.score} />
              <DivergingBar score={row.score} />
            </Box>

            {isExpanded && (
              <Box sx={{ pb: 2, pt: 0.5, borderBottom: i === rows.length - 1 ? "none" : "1px solid #f5f6f8" }}>
                {row.subRows ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {row.subRows.map((sub) => {
                      if (!sub.entityData?.items?.length) return null;
                      const isSubCollapsed = collapsedSubLabels.has(sub.label);
                      return (
                        // The indent + left guide line (standard tree/outline nesting
                        // signal, reusing #eef1f4, the divider color this component
                        // already uses elsewhere) lives INSIDE the topic-name cell only
                        // — not on a wrapping Box around the whole grid — so it never
                        // shifts the grid's own column tracks. Score badge + diverging
                        // bar reuse the exact same gridTemplateColumns as a top-level
                        // row, so both land in the identical x-position as the parent
                        // row's columns and stay directly, visually comparable.
                        <Box key={sub.label}>
                          <Box
                            onClick={() => toggleSubsection(sub.label)}
                            sx={{
                              display: "grid",
                              gridTemplateColumns,
                              gap: 1.75,
                              alignItems: "center",
                              py: 1,
                              cursor: "pointer",
                              userSelect: "none",
                              "&:hover": { bgcolor: "#fafbfc" },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 2, borderLeft: "2px solid #eef1f4" }}>
                              <KeyboardArrowRightIcon
                                sx={{ fontSize: 16, color: "#98a2b3", transform: isSubCollapsed ? "none" : "rotate(90deg)", transition: "transform 0.15s" }}
                              />
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475467" }}>
                                {sub.label}
                              </Typography>
                            </Box>
                            <ScoreBadge score={sub.score} />
                            <DivergingBar score={sub.score} compact />
                          </Box>
                          {!isSubCollapsed && (
                            <Box sx={{ pb: 1, pl: 2.5 }}>
                              <TopicItemTable topicLabel={sub.label} entityData={sub.entityData} bare />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <TopicItemTable topicLabel={row.label} entityData={row.entityData} bare />
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {rows.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "#667085", py: 2 }}>No scored topics for this run yet.</Typography>
      )}
    </Box>
  );
}
